import { useState, useCallback } from 'react'
import { Pencil, Pill, Plus, Sparkles, Trash2, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { SearchableCombobox } from '@/components/ui/searchable-combobox'
import { toast } from 'sonner'
import { commonPrescriptions } from '@/data/common-prescriptions'
import type { PrescriptionTemplate } from '@/data/common-prescriptions'
import type { Prescription, DocumentationAction } from '@/types/documentation'

interface PrescriptionPanelProps {
  prescriptions: Prescription[]
  dispatch: React.Dispatch<DocumentationAction>
  embedded?: boolean
}

interface PrescriptionFormData {
  medication: string
  dosage: string
  quantity: string
  refills: number
  duration: string
  instructions: string
}

const prescriptionFilterFn = (p: PrescriptionTemplate, q: string) =>
  p.medication.toLowerCase().includes(q) ||
  p.category.toLowerCase().includes(q) ||
  p.dosage.toLowerCase().includes(q)

function PrescriptionCombobox({
  onSelect,
  onCustom,
}: {
  onSelect: (template: PrescriptionTemplate) => void
  onCustom: () => void
}) {
  const renderItem = useCallback(
    (p: PrescriptionTemplate) => (
      <>
        <span className="text-xs font-medium text-slate-900">{p.medication}</span>
        <span className="text-xs text-slate-500">
          {p.dosage} · Qty: {p.quantity}
          {p.refills > 0 && ` · ${p.refills} refills`}
        </span>
      </>
    ),
    [],
  )

  return (
    <SearchableCombobox
      items={commonPrescriptions}
      filterFn={prescriptionFilterFn}
      onSelect={onSelect}
      renderItem={renderItem}
      trigger={
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add Prescription
        </Button>
      }
      placeholder="Search prescriptions..."
      searchLabel="Search prescriptions"
      resultsLabel="Prescription search results"
      emptyText="No matching prescriptions"
      footer={(close) => (
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs"
          onClick={() => {
            onCustom()
            close()
          }}
        >
          <Plus className="h-3 w-3 mr-1" />
          Custom prescription...
        </Button>
      )}
    />
  )
}

function PrescriptionForm({
  initial,
  onSave,
  onCancel,
  saveLabel,
}: {
  initial?: Prescription
  onSave: (data: PrescriptionFormData) => void
  onCancel: () => void
  saveLabel?: string
}) {
  const [medication, setMedication] = useState(initial?.medication ?? '')
  const [dosage, setDosage] = useState(initial?.dosage ?? '')
  const [quantity, setQuantity] = useState(initial?.quantity ?? '')
  const [refills, setRefills] = useState(String(initial?.refills ?? 0))
  const [duration, setDuration] = useState(initial?.duration ?? '')
  const [instructions, setInstructions] = useState(initial?.instructions ?? '')

  const isValid = medication.trim().length > 0 && dosage.trim().length > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    const parsedRefills = parseInt(refills, 10)
    onSave({
      medication: medication.trim(),
      dosage: dosage.trim(),
      quantity: quantity.trim(),
      refills: Number.isNaN(parsedRefills) || parsedRefills < 0 ? 0 : parsedRefills,
      duration: duration.trim(),
      instructions: instructions.trim(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-md border p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label htmlFor="rx-medication" className="text-xs">
            Medication
          </Label>
          <Input
            id="rx-medication"
            value={medication}
            onChange={(e) => setMedication(e.target.value)}
            placeholder="e.g., Tretinoin Cream 0.025%"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="rx-dosage" className="text-xs">
            Dosage
          </Label>
          <Input
            id="rx-dosage"
            value={dosage}
            onChange={(e) => setDosage(e.target.value)}
            placeholder="e.g., Apply QHS"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="rx-duration" className="text-xs">
            Duration
          </Label>
          <Input
            id="rx-duration"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="e.g., 12 weeks"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="rx-quantity" className="text-xs">
            Quantity
          </Label>
          <Input
            id="rx-quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="e.g., 45g"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="rx-refills" className="text-xs">
            Refills
          </Label>
          <Input
            id="rx-refills"
            type="number"
            min="0"
            value={refills}
            onChange={(e) => setRefills(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="rx-instructions" className="text-xs">
            Instructions
          </Label>
          <Input
            id="rx-instructions"
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="e.g., Apply to clean, dry skin at bedtime"
            className="mt-1"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" type="submit" disabled={!isValid}>
          {saveLabel ?? 'Save'}
        </Button>
        <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default function PrescriptionPanel({
  prescriptions,
  dispatch,
  embedded,
}: PrescriptionPanelProps) {
  const [showCustomForm, setShowCustomForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const handleDoseSpot = () => {
    toast.info('DoseSpot integration coming soon', {
      description: 'E-prescribing will be available in a future update.',
    })
  }

  const isDuplicate = (medication: string, excludeId?: string) => {
    return prescriptions.some(
      (p) =>
        p.medication.toLowerCase() === medication.toLowerCase() &&
        p.id !== excludeId,
    )
  }

  const handleSelectTemplate = (template: PrescriptionTemplate) => {
    if (isDuplicate(template.medication)) {
      toast.warning(`${template.medication} is already in the list`)
      return
    }
    dispatch({
      type: 'ADD_PRESCRIPTION',
      prescription: {
        id: crypto.randomUUID(),
        medication: template.medication,
        dosage: template.dosage,
        quantity: template.quantity,
        refills: template.refills,
        duration: template.duration,
        instructions: template.instructions,
      },
    })
  }

  const handleCustomSave = (data: PrescriptionFormData) => {
    if (isDuplicate(data.medication)) {
      toast.warning(`${data.medication} is already in the list`)
      return
    }
    dispatch({
      type: 'ADD_PRESCRIPTION',
      prescription: {
        id: crypto.randomUUID(),
        ...data,
      },
    })
    setShowCustomForm(false)
  }

  const handleEditSave = (
    id: string,
    isAiGenerated: boolean | undefined,
    data: PrescriptionFormData,
  ) => {
    if (isDuplicate(data.medication, id)) {
      toast.warning(`${data.medication} is already in the list`)
      return
    }
    dispatch({
      type: 'UPDATE_PRESCRIPTION',
      prescription: { id, ...data, isAiGenerated },
    })
    setEditingId(null)
  }

  const content = (
    <>
      {prescriptions.length > 0 && (
        <div className="space-y-2">
          {prescriptions.map((rx) =>
            editingId === rx.id ? (
              <PrescriptionForm
                key={rx.id}
                initial={rx}
                saveLabel="Update"
                onSave={(data) => handleEditSave(rx.id, rx.isAiGenerated, data)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div
                key={rx.id}
                className="flex items-center justify-between rounded-md border p-3"
              >
                <div className="flex items-center gap-3">
                  <Pill className="h-4 w-4 text-slate-400" />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-900">
                        {rx.medication}
                      </p>
                      {rx.isAiGenerated && (
                        <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                          <Sparkles className="h-3 w-3" /> AI
                        </span>
                      )}
                      {(!rx.quantity || !rx.dosage) && (
                        <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                          Incomplete
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">
                      {rx.dosage}
                      {rx.duration && ` · ${rx.duration}`}
                      {rx.quantity && ` · Qty: ${rx.quantity}`}
                      {rx.refills > 0 && ` · ${rx.refills} refill${rx.refills !== 1 ? 's' : ''}`}
                    </p>
                    {rx.instructions && (
                      <p className="text-xs text-slate-400 mt-0.5">{rx.instructions}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingId(rx.id)}
                    aria-label={`Edit ${rx.medication}`}
                  >
                    <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dispatch({ type: 'REMOVE_PRESCRIPTION', id: rx.id })}
                    aria-label={`Remove ${rx.medication}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                  </Button>
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {showCustomForm ? (
        <PrescriptionForm
          onSave={handleCustomSave}
          onCancel={() => setShowCustomForm(false)}
        />
      ) : (
        <div className="flex gap-2">
          <PrescriptionCombobox
            onSelect={handleSelectTemplate}
            onCustom={() => setShowCustomForm(true)}
          />
        </div>
      )}
    </>
  )

  if (embedded) {
    return <div className="space-y-4">{content}</div>
  }

  return (
    <div className="rounded-lg border bg-white p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Prescriptions</h3>
        <Button variant="outline" size="sm" onClick={handleDoseSpot}>
          <ExternalLink className="h-4 w-4 mr-1" />
          Open DoseSpot
        </Button>
      </div>
      {content}
    </div>
  )
}
